/**
 * Casa del Bacalao · "Compra los ingredientes de esta receta" (blocks/casa-receta-compra.liquid)
 *
 * <casa-receta-compra> suma lo marcado (centavos enteros, sin floats) y, al pulsar el botón, mete
 * todos los ingredientes al carrito con UNA petición a /cart/add.js (items: [...]).
 *
 * Carrito lateral: igual que assets/product-form.js — pide las secciones de cada
 * <cart-items-component> (data-section-id), despacha CartLinesUpdateEvent con createPromise() y la
 * resuelve con el carrito + las secciones de la respuesta. component-cart-items.js re-dibuja el
 * drawer y cart-drawer.js lo abre (si tiene auto-open), cart-icon.js actualiza la burbuja.
 *
 * Si Shopify rechaza el lote (p. ej. un ingrediente agotado, 422), se compara el carrito de antes
 * con el de después para no duplicar lo que sí entró, se reintenta el resto de uno en uno, y cada
 * renglón que falle muestra el mensaje de Shopify en texto claro.
 */

import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Centavos enteros → "$1,428" (sin decimales si no hay centavos) o "$25.97".
 * @param {number} centavos
 * @returns {string}
 */
export function formatoCentavos(centavos) {
  const c = Math.max(0, Math.trunc(Number(centavos) || 0));
  const resto = c % 100;
  const pesos = (c - resto) / 100;
  const base = `$${pesos.toLocaleString('es-MX')}`;
  return resto ? `${base}.${String(resto).padStart(2, '0')}` : base;
}

/**
 * Suma en centavos de las líneas marcadas (precio × cantidad de cada parte, enteros).
 * @param {{ marcada: boolean, partes: { precio: number, cantidad: number }[] }[]} lineas
 * @returns {number}
 */
export function totalCentavos(lineas) {
  let total = 0;
  for (const l of lineas) {
    if (!l.marcada) continue;
    for (const p of l.partes) total += p.precio * p.cantidad;
  }
  return total;
}

const RE_MEDIDA = /(\d+(?:[.,]\d+)?)\s*(kg|kilos?|gramos|grs?|g|ml|litros?|lts?|l)\b/i;

/**
 * Peso o volumen en el texto ("1kg", "500 gr", "750ml", "Tarro 820 g", "1 Litro").
 * @param {string} texto
 * @returns {{ cantidad: number, unidad: 'g' | 'ml' } | null}
 */
export function medida(texto) {
  const m = String(texto || '').match(RE_MEDIDA);
  if (!m) return null;
  const n = parseFloat(m[1].replace(',', '.'));
  if (!(n > 0)) return null;
  const u = m[2].toLowerCase();
  if (u.startsWith('k')) return { cantidad: Math.round(n * 1000), unidad: 'g' };
  if (u === 'ml') return { cantidad: Math.round(n), unidad: 'ml' };
  if (u === 'l' || u.startsWith('lt') || u.startsWith('litro')) return { cantidad: Math.round(n * 1000), unidad: 'ml' };
  return { cantidad: Math.round(n), unidad: 'g' };
}

/**
 * Menor total que alcance el objetivo; empate → menos piezas; empate → más barato.
 * @template {{ g: number, precio: number }} V
 * @param {V[]} variantes
 * @param {number} objetivo
 * @returns {(V & { n: number })[]}
 */
export function combinar(variantes, objetivo) {
  const vs = [...variantes].filter((v) => v.g > 0).sort((a, b) => b.g - a.g).slice(0, 3);
  if (!vs.length || !(objetivo > 0)) return [];
  /** @type {{ cuenta: number[], total: number, piezas: number, precio: number } | null} */
  let mejor = null;
  const cuenta = new Array(vs.length).fill(0);
  const rec = (/** @type {number} */ i, /** @type {number} */ total, /** @type {number} */ piezas, /** @type {number} */ precio) => {
    if (i === vs.length) {
      if (total < objetivo) return;
      if (
        !mejor ||
        total < mejor.total ||
        (total === mejor.total && (piezas < mejor.piezas || (piezas === mejor.piezas && precio < mejor.precio)))
      ) {
        mejor = { cuenta: [...cuenta], total, piezas, precio };
      }
      return;
    }
    const v = vs[i];
    const max = Math.ceil(Math.max(0, objetivo - total) / v.g);
    for (let n = 0; n <= max; n++) {
      cuenta[i] = n;
      rec(i + 1, total + n * v.g, piezas + n, precio + n * v.precio);
    }
    cuenta[i] = 0;
  };
  rec(0, 0, 0, 0);
  if (!mejor) return [];
  const elegido = /** @type {{ cuenta: number[] }} */ (mejor);
  return vs.map((v, i) => ({ ...v, n: elegido.cuenta[i] })).filter((v) => v.n > 0);
}

/** @param {number} cantidad @param {'g' | 'ml'} unidad */
function etiquetaMedida(cantidad, unidad) {
  if (cantidad >= 1000) {
    const k = cantidad / 1000;
    return `${Number.isInteger(k) ? k : k.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')} ${unidad === 'ml' ? 'l' : 'kg'}`;
  }
  return `${cantidad} ${unidad}`;
}

/**
 * Cuánto de cada variante falta por agregar, comparando el carrito antes y después de un intento.
 * @param {{ id: string, cantidad: number }[]} lineas
 * @param {Record<string, number>} antes - cantidad por variant_id
 * @param {Record<string, number>} despues - cantidad por variant_id
 * @returns {{ entraron: typeof lineas, faltan: typeof lineas }}
 */
export function repartirPorCarrito(lineas, antes, despues) {
  const entraron = [];
  const faltan = [];
  for (const l of lineas) {
    const subio = (despues[l.id] || 0) - (antes[l.id] || 0);
    if (subio >= l.cantidad) entraron.push(l);
    else faltan.push(l);
  }
  return { entraron, faltan };
}

/**
 * @param {{ items?: { variant_id: number, quantity: number }[] }} carrito
 * @returns {Record<string, number>}
 */
function cantidadesPorVariante(carrito) {
  /** @type {Record<string, number>} */
  const mapa = {};
  for (const item of carrito?.items ?? []) {
    const id = String(item.variant_id);
    mapa[id] = (mapa[id] || 0) + item.quantity;
  }
  return mapa;
}

/**
 * @param {string} plantilla
 * @param {Record<string, string | number>} valores
 */
function rellenar(plantilla, valores) {
  return Object.entries(valores).reduce((t, [k, v]) => t.split(`[${k}]`).join(String(v)), plantilla || '');
}

class CasaRecetaCompra extends HTMLElement {
  #ocupado = false;

  connectedCallback() {
    this.boton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-crc-boton]'));
    this.personasInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-crc-personas]'));
    this.addEventListener('change', this.#alCambiar);
    this.addEventListener('click', this.#alClic);
    this.boton?.addEventListener('click', this.#alAgregar);
    this.#recalcular(false);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#alCambiar);
    this.removeEventListener('click', this.#alClic);
    this.boton?.removeEventListener('click', this.#alAgregar);
  }

  get #personas() {
    const n = parseInt(this.personasInput?.value || '', 10);
    const base = parseInt(this.dataset.personasBase || '6', 10) || 6;
    return Math.min(40, Math.max(1, Number.isFinite(n) ? n : base));
  }

  /** @param {number} n */
  #ponerPersonas(n) {
    const v = Math.min(40, Math.max(1, n));
    if (this.personasInput) this.personasInput.value = String(v);
    const menos = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-crc-menos]'));
    const mas = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-crc-mas]'));
    if (menos) menos.disabled = v <= 1;
    if (mas) mas.disabled = v >= 40;
  }

  /** @param {MouseEvent} event */
  #alClic = (event) => {
    const b = /** @type {HTMLElement} */ (event.target).closest('[data-crc-menos], [data-crc-mas]');
    if (!b) return;
    this.#ponerPersonas(this.#personas + (b.hasAttribute('data-crc-mas') ? 1 : -1));
    this.#recalcular(true);
  };

  /**
   * Partes (variante × cantidad) de un renglón para las personas elegidas.
   * @param {HTMLElement} fila
   * @param {number} personas
   * @returns {{ partes: { id: string, cantidad: number, precio: number }[], texto: string }}
   */
  #calcularFila(fila, personas) {
    const gpp = parseInt(this.dataset.gramosPersona || '200', 10) || 200;
    const bacalaoReceta = parseInt(this.dataset.bacalaoReceta || '0', 10) || 0;
    const base = parseInt(this.dataset.personasBase || '6', 10) || 6;
    const factor = bacalaoReceta ? (personas * gpp) / bacalaoReceta : personas / base;

    /** @type {{ id: number, precio: number, t: string }[]} */
    let crudas = [];
    try {
      crudas = JSON.parse(fila.dataset.variantes || '[]');
    } catch {
      crudas = [];
    }
    const deTitulo = medida(fila.dataset.titulo || '');
    const variantes = crudas.map((v) => {
      const m = medida(v.t) || (crudas.length === 1 ? deTitulo : null);
      return { id: String(v.id), precio: Number(v.precio) || 0, g: m ? m.cantidad : 0, unidad: m ? m.unidad : 'g' };
    });
    if (!variantes.length) return { partes: [], texto: '' };
    const medidas = variantes.filter((v) => v.g > 0);
    const chica = medidas.length ? [...medidas].sort((a, b) => a.g - b.g)[0] : variantes[0];

    /** @type {number | null} */
    let objetivo = null;
    let piezas = 1;
    if (fila.hasAttribute('data-bacalao')) objetivo = personas * gpp;
    else if (fila.hasAttribute('data-fija')) piezas = 1;
    else if (fila.dataset.receta) objetivo = (parseFloat(fila.dataset.receta) || 0) * factor;
    else piezas = Math.max(1, Math.ceil(factor - 1e-9));

    /** @type {{ id: string, precio: number, g: number, unidad: string, n: number }[]} */
    let elegidas = [];
    if (objetivo !== null && medidas.length) elegidas = combinar(medidas, Math.round(objetivo));
    if (!elegidas.length) elegidas = [{ ...chica, n: piezas }];

    const texto = elegidas
      .map((v) => {
        const etiqueta = v.g > 0 ? etiquetaMedida(v.g, /** @type {'g' | 'ml'} */ (v.unidad)) : '';
        if (!etiqueta) return v.n === 1 ? '1 pieza' : `${v.n} piezas`;
        return v.n === 1 ? etiqueta : `${v.n} × ${etiqueta}`;
      })
      .join(' + ');
    return { partes: elegidas.map((v) => ({ id: v.id, cantidad: v.n, precio: v.precio })), texto };
  }

  /** @param {boolean} anunciar */
  #recalcular(anunciar) {
    const personas = this.#personas;
    this.#ponerPersonas(personas);
    for (const fila of this.#filas) {
      const { partes, texto } = this.#calcularFila(fila, personas);
      fila.dataset.partes = JSON.stringify(partes);
      const pres = fila.querySelector('[data-crc-pres]');
      if (pres) pres.textContent = texto;
      const precio = fila.querySelector('[data-crc-precio]');
      if (precio) precio.textContent = formatoCentavos(partes.reduce((s, p) => s + p.precio * p.cantidad, 0));
    }
    this.#pintar(anunciar);
  }

  /** @returns {HTMLElement[]} */
  get #filas() {
    return Array.from(this.querySelectorAll('[data-crc-fila]'));
  }

  /** @param {HTMLElement} fila */
  #linea(fila) {
    const check = /** @type {HTMLInputElement | null} */ (fila.querySelector('[data-crc-check]'));
    /** @type {{ id: string, cantidad: number, precio: number }[]} */
    let partes = [];
    try {
      partes = JSON.parse(fila.dataset.partes || '[]');
    } catch {
      partes = [];
    }
    if (!partes.length) {
      partes = [
        {
          id: String(fila.dataset.variante),
          cantidad: Math.max(1, parseInt(fila.dataset.cantidad || '1', 10) || 1),
          precio: parseInt(fila.dataset.precio || '0', 10) || 0,
        },
      ];
    }
    return { fila, marcada: Boolean(check?.checked), partes, nombre: fila.dataset.nombre || '' };
  }

  #lineas() {
    return this.#filas.map((fila) => this.#linea(fila));
  }

  /** @param {Event} event */
  #alCambiar = (event) => {
    const objetivo = /** @type {HTMLElement} */ (event.target);
    if (objetivo.matches('[data-crc-personas]')) {
      this.#recalcular(true);
      return;
    }
    const fila = /** @type {HTMLElement | null} */ (objetivo.closest('[data-crc-fila]'));
    if (!fila) return;
    this.#errorFila(fila, '');
    this.#pintar(true);
  };

  /** @param {boolean} anunciar */
  #pintar(anunciar) {
    if (!this.boton) return;
    const lineas = this.#lineas();
    const marcadas = lineas.filter((l) => l.marcada);
    const n = marcadas.length;
    const total = formatoCentavos(totalCentavos(lineas));

    const texto = this.querySelector('[data-crc-texto]');
    const sep = /** @type {HTMLElement | null} */ (this.querySelector('[data-crc-sep]'));
    const totalEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-crc-total]'));

    if (texto) {
      texto.textContent =
        n === 0
          ? this.dataset.textoVacio || ''
          : n === 1
            ? this.dataset.textoSingular || ''
            : rellenar(this.dataset.textoPlural || '', { n });
    }
    if (sep) sep.hidden = n === 0;
    if (totalEl) {
      totalEl.hidden = n === 0;
      totalEl.textContent = total;
    }
    if (!this.#ocupado) this.boton.disabled = n === 0;

    if (anunciar) {
      const vivo = this.querySelector('[data-crc-vivo]');
      if (vivo) vivo.textContent = n === 0 ? this.dataset.textoVacio || '' : `${texto?.textContent ?? ''}, total ${total}`;
    }
  }

  /**
   * @param {HTMLElement} fila
   * @param {string} mensaje
   */
  #errorFila(fila, mensaje) {
    const el = /** @type {HTMLElement | null} */ (fila.querySelector('[data-crc-error]'));
    if (!el) return;
    el.textContent = mensaje;
    el.hidden = !mensaje;
  }

  /**
   * @param {string} mensaje
   * @param {'ok' | 'error'} tipo
   */
  #estado(mensaje, tipo) {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('[data-crc-estado]'));
    if (!el) return;
    el.textContent = mensaje;
    el.dataset.tipo = tipo;
    el.hidden = !mensaje;
  }

  /** Secciones del carrito que Horizon re-dibuja (drawer y página de carrito). */
  #secciones() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) ids.add(item.dataset.sectionId);
    });
    return Array.from(ids);
  }

  async #leerCarrito() {
    const componente = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (componente) {
      await customElements.whenDefined('cart-items-component');
      if (typeof componente.fetchCartData === 'function') return componente.fetchCartData();
    }
    const respuesta = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!respuesta.ok) throw new Error(`No se pudo leer el carrito: ${respuesta.status}`);
    return respuesta.json();
  }

  /**
   * Una petición a /cart/add.js con varias líneas.
   * @param {{ id: string, cantidad: number }[]} lineas
   * @param {string[]} secciones
   * @returns {Promise<{ ok: true, sections?: Record<string, string> } | { ok: false, mensaje: string }>}
   */
  async #agregar(lineas, secciones) {
    /** @type {Record<string, unknown>} */
    const cuerpo = {
      items: lineas.map((l) => ({ id: Number(l.id), quantity: l.cantidad })),
    };
    if (secciones.length) cuerpo.sections = secciones.join(',');

    const respuesta = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(cuerpo) }));
    const datos = await respuesta.json().catch(() => ({}));

    if (!respuesta.ok || datos.status) {
      return { ok: false, mensaje: String(datos.description || datos.message || this.dataset.textoError || '') };
    }
    return { ok: true, sections: datos.sections };
  }

  #alAgregar = async () => {
    if (this.#ocupado || !this.boton) return;
    const lineas = this.#lineas()
      .filter((l) => l.marcada)
      .flatMap((l) => l.partes.map((p) => ({ fila: l.fila, nombre: l.nombre, id: p.id, cantidad: p.cantidad })));
    if (!lineas.length) return;

    this.#ocupado = true;
    this.boton.disabled = true;
    this.boton.setAttribute('aria-busy', 'true');
    this.#estado('', 'ok');
    for (const fila of this.#filas) this.#errorFila(fila, '');

    const secciones = this.#secciones();
    const diferido = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lineas.map((l) => ({ merchandiseId: l.id, quantity: l.cantidad })),
        promise: diferido.promise,
      })
    );

    try {
      const antes = cantidadesPorVariante(await this.#leerCarrito());

      /** @type {typeof lineas} */
      let entraron = [];
      /** @type {{ linea: (typeof lineas)[number], mensaje: string }[]} */
      const fallidas = [];
      /** @type {Record<string, string> | undefined} */
      let htmlSecciones;

      const lote = await this.#agregar(lineas, secciones);
      if (lote.ok) {
        entraron = lineas;
        htmlSecciones = lote.sections;
      } else {
        // Lote rechazado: lo que ya haya entrado no se repite; el resto va de uno en uno.
        const reparto = repartirPorCarrito(lineas, antes, cantidadesPorVariante(await this.#leerCarrito()));
        entraron = reparto.entraron;
        for (const linea of reparto.faltan) {
          const uno = await this.#agregar([linea], secciones);
          if (uno.ok) {
            entraron.push(linea);
            htmlSecciones = uno.sections ?? htmlSecciones;
          } else {
            fallidas.push({ linea, mensaje: uno.mensaje });
          }
        }
      }

      const carrito = await this.#leerCarrito();
      diferido.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
        detail: {
          items: carrito.items,
          source: 'casa-receta-compra',
          sourceId: this.id || 'casa-receta-compra',
          itemCount: entraron.reduce((s, l) => s + l.cantidad, 0),
          sections: htmlSecciones,
          didError: entraron.length === 0,
        },
      });

      for (const { linea, mensaje } of fallidas) {
        this.#errorFila(linea.fila, mensaje);
        this.dispatchEvent(new CartErrorEvent({ error: mensaje || 'Add to cart failed', code: 'INVALID' }));
      }

      if (!fallidas.length) {
        this.#estado(rellenar(this.dataset.textoListo || '', { n: new Set(entraron.map((l) => l.fila)).size }), 'ok');
      } else {
        this.#estado(
          rellenar(this.dataset.textoParcial || '', { n: new Set(entraron.map((l) => l.fila)).size, m: new Set(fallidas.map((f) => f.linea.fila)).size }),
          entraron.length ? 'ok' : 'error'
        );
      }
    } catch (error) {
      console.error(error);
      diferido.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {Error} */ (error)?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#estado(this.dataset.textoError || '', 'error');
    } finally {
      this.#ocupado = false;
      this.boton.removeAttribute('aria-busy');
      this.#pintar(false);
    }
  };
}

if (!customElements.get('casa-receta-compra')) {
  customElements.define('casa-receta-compra', CasaRecetaCompra);
}
